<template>
  <!-- Block boutons trie et saisie en masse -->
  <div class="form__bloc">
    <div class="action-bar col-36">
      <BasicText class="action-title mobile-none">Éditer les frais annexes par :</BasicText>
      <div class="action-buttons">
        <BasicToggleSwitch
          v-model="modeAffichage"
          :items="[
            { id: 'modules', label: 'Par Modules' },
            { id: 'salaries', label: 'Par Salariés' },
          ]"
          :disabled="fraisAnnexes.length === 0"
          class="btn-switch"
        />
        <BasicButton
          v-if="estSaisieEnMasseVisible"
          label="Saisir en masse"
          icon-name="gear"
          variant="outlined"
          icon-right
          class="btn-action"
          @click="showBatchUpdate"
        ></BasicButton>
      </div>
    </div>
    <!-- Alertes -->
    <BasicAlert
      v-if="meta?.fraisAnnexes?.$messageErreurDirty"
      :alertLabel="meta?.fraisAnnexes?.$messageErreurDirty"
      alertType="danger"
      class="col-36 warning-couts"
    />
    <WarningCoutsFormation v-else class="col-36 warning-couts" />
  </div>
  <!-- Block aucun frais annexe -->
  <FormContainer
    v-if="!fraisAnnexes.length"
    without-divider
  >
    <template #title>Aucun salarié ou module de formation</template>
    <template #content>
      <EmptyDataTable>
        <template #content>
          Retrouvez ici la saisie des
          <strong>frais annexes</strong>
          .
          <br />
          <br />
          <BasicText font-style="italic">
            Ajouter au moins un module et salarie pour activer la saisie des frais annexes.
          </BasicText>
        </template>
      </EmptyDataTable>
    </template>
  </FormContainer>
  <!-- Bock tableau des frais annexes -->
  <FormContainer
    v-for="fa in fraisAnnexes"
    :key="fa.id"
    without-divider
  >
    <template #title>
      {{ fa.intitule }}
    </template>
    <template #content>
      <!-- DESKTOP -->
      <div class="mobile-none tablet-none desktop">
        <div class="table-frais">
          <!-- Header -->
          <div class="header">
            <div class="table-col table-col-header flex-medium">
              <div class="table-cell">
                <BasicText
                  weight="bold"
                  class="table-content"
                >
                  <template v-if="modeAffichage === 'modules'">Salariés</template>
                  <template v-else>Modules</template>
                </BasicText>
              </div>
            </div>
            <div
              v-for="nf in naturesFrais"
              :key="nf.nature"
              class="table-col"
              :class="nf.baseCalcul === 'Quantite' ? 'flex-large' : 'flex-medium'"
            >
              <div class="table-cell">
                <BasicText
                  class="table-content"
                  weight="bold"
                  nb-max-lines="2"
                >
                  {{ nf.intitule }}
                </BasicText>
              </div>
              <div class="table-cell">
                <BasicText
                  v-if="nf.baseCalcul === 'Quantite'"
                  class="table-content"
                >
                  {{ nf.labelQuantite }}
                </BasicText>
                <BasicText class="table-content">
                  {{ nf.labelMontant }} (€ HT)
                </BasicText>
              </div>
            </div>
          </div>
          <!-- Ligne de frais -->
          <div
            class="table-row"
            v-for="row in fa.rows"
            :key="row.id"
          >
            <div class="table-cell cell-header flex-medium">
              <BasicText class="table-content" nb-max-lines="2">
                {{ row.intitule }}
              </BasicText>
            </div>
            <div
              v-for="frais in row.frais"
              :key="frais.nature!"
              class="table-cell"
              :class="frais.baseCalcul === 'Quantite' ? 'flex-large' : 'flex-medium'"
            >
              <WrapperInputNumber
                v-if="frais.baseCalcul === 'Quantite'"
                class="table-content"
                v-model="frais.quantite"
                :readonly
                :alertLabel="frais.$meta.quantite?.$messageErreurDirty"
                :decimal="0"
              />
              <WrapperInputNumber
                class="table-content"
                v-model="frais.montant"
                :readonly
                :alertLabel="frais.$meta.montant?.$messageErreurDirty"
                :decimal="2"
              />
            </div>
          </div>
          <!-- Ligne total par frais -->
          <div class="table-row footer-total">
            <div class="table-cell cell-header flex-medium">
              <BasicText
                weight="bold"
                class="table-content"
              >
                Total: {{ formatNumber(calculFraisTotal(fa), { decimal: 2, separateur: true }) ?? '-' }} € HT
              </BasicText>
            </div>
            <div
              v-for="nf in naturesFrais"
              :key="nf.nature"
              class="table-cell"
              :class="nf.baseCalcul === 'Quantite' ? 'flex-large' : 'flex-medium'"
            >
              <div
                class="table-content"
                v-if="nf.baseCalcul === 'Quantite'"
              ></div>
              <WrapperInputNumber
                class="table-content"
                :modelValue="calculFraisParNature(fa, nf.nature)"
                :decimal="2"
                separateur
                disabled
              />
            </div>
          </div>
        </div>
      </div>
      <!-- MOBILE -->
      <div class="desktop-none mobile">
        <div class="table-frais">
          <template
            v-for="nf in naturesFrais"
            :key="nf.nature"
          >
            <!-- Header -->
            <div class="header">
              <div class="table-cell flex-large">
                <BasicText
                  class="table-content"
                  weight="bold"
                  size="body-m"
                  nb-max-lines="2"
                >
                  {{ nf.intitule }}
                </BasicText>
              </div>
              <div class="table-cell flex-medium">
                <BasicText
                  v-if="nf.baseCalcul === 'Quantite'"
                  class="table-content align-right"
                  size="body-m"
                >
                  {{ nf.labelQuantite }}
                </BasicText>
              </div>
              <div class="table-cell flex-large">
                <BasicText
                  class="table-content align-right"
                  size="body-m"
                >
                  {{ nf.labelMontant }} (€ HT)
                </BasicText>
              </div>
            </div>
            <!-- Ligne de frais -->
            <div
              class="table-row"
              v-for="row in fa.rows"
              :key="row.id"
            >
              <div class="table-cell table-row-header flex-large">
                <BasicText size="body-m" nb-max-lines="2">
                  {{ row.intitule }}
                </BasicText>
              </div>
              <div class="table-cell flex-medium">
                <WrapperInputNumber
                  v-if="getFraisNature(row, nf.nature).baseCalcul === 'Quantite'"
                  v-model="getFraisNature(row, nf.nature).quantite"
                  :readonly
                  :alertLabel="getFraisNature(row, nf.nature).$meta.quantite?.$messageErreurDirty"
                  class="table-content"
                  :decimal="0"
                />
              </div>
              <div class="table-cell flex-large">
                <WrapperInputNumber
                  v-model="getFraisNature(row, nf.nature).montant"
                  class="table-content"
                  :decimal="2"
                  :readonly
                  :alertLabel="getFraisNature(row, nf.nature).$meta.montant?.$messageErreurDirty"
                />
              </div>
            </div>
            <!-- Ligne total par nature -->
            <div class="table-row footer-total">
              <div class="table-cell table-row-header flex-large">
                <BasicText
                  weight="bold"
                  class="table-content"
                  size="body-m"
                >
                  Total HT
                </BasicText>
              </div>
              <div class="table-cell flex-medium"></div>
              <div class="table-cell flex-large">
                <WrapperInputNumber
                  class="table-content"
                  :modelValue="calculFraisParNature(fa, nf.nature)"
                  :decimal="2"
                  separateur
                  disabled
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </FormContainer>
</template>
<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { formatNumber, isNullOrUndefined } from '@/features/shared/tools'
  import type { NatureFraisAnnexe } from '@/features/domaine/types/opco/Demande/AF'
  import type { FraisAnnexeModele, FraisAnnexeRowModele } from '@/features/interface/demande/af/steps/frais/types.ts'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore.ts'
  import { AF_FRAIS_ANNEXES_ACTIONS } from '@/features/interface/demande/af/steps/frais/useFraisAnnexesActions.ts'
  import { EmptyDataTable, FormContainer } from '@/features/interface/demande/shared'
  import WarningCoutsFormation from '@/features/interface/demande/af/shared/WarningCoutsFormation.vue'

  const store = useDemandeAFStore()
  const { readonly, model, meta } = storeToRefs(store)
  const fraisAction = inject(AF_FRAIS_ANNEXES_ACTIONS)!
  const { fraisAnnexes, modeAffichage, naturesFrais, showBatchUpdate } = fraisAction

  const calculFraisTotal = (fraisAnnexe: FraisAnnexeModele) =>
    fraisAnnexe.rows
      .flatMap((fa) => fa.frais)
      .reduce<number | null>((total, frais) => {
        if (isNullOrUndefined(frais?.total)) return total
        return (total ?? 0) + frais.total
      }, null)

  const calculFraisParNature = (fraisAnnexe: FraisAnnexeModele, nature: NatureFraisAnnexe) => {
    return fraisAnnexe.rows.reduce<number | null>((total, row) => {
      const frais = row.frais.find((f) => f.nature === nature)
      if (isNullOrUndefined(frais?.total)) return total
      return (total ?? 0) + frais.total
    }, null)
  }

  const getFraisNature = (frais: FraisAnnexeRowModele, nature: NatureFraisAnnexe) =>
    frais.frais.find((f) => f.nature === nature)!

  const estSaisieEnMasseVisible = computed(() => {
    if (readonly.value) return false
    return (model.value?.salaries?.length ?? 0) > 1
  })
</script>
<style lang="less" scoped>
  @import './frais.less';
</style>
